import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/components/common/Toast';
import { orderService } from '@/services/order';
import { tossPaymentsService } from '@/services/tossPayments';
import { useAuth } from '@/contexts/AuthContext';
import { DesktopPageContainer } from '@/components/desktop/DesktopPageContainer';

export function DesktopPaymentFailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    const handlePaymentFailure = async () => {
      // URL 파라미터에서 실패 정보 추출
      const code = searchParams.get('code');
      const message = searchParams.get('message');
      const orderId = searchParams.get('orderId');

      console.error('Payment failed:', { code, message, orderId });

      // 에러 메시지 표시
      let errorMessage = '결제에 실패했습니다';
      
      if (code === 'USER_CANCEL') {
        errorMessage = '결제를 취소하셨습니다';
      } else if (code === 'INVALID_CARD_COMPANY') {
        errorMessage = '유효하지 않은 카드입니다';
      } else if (code === 'INSUFFICIENT_BALANCE') {
        errorMessage = '잔액이 부족합니다';
      } else if (message) {
        errorMessage = message;
      }

      // Pending 상태의 주문이 있는 경우 취소 처리 및 장바구니 상태 복원
      if (orderId && user) {
        try {
          // 주문 정보 조회하여 order_type과 cart_id 확인
          const orderData = await orderService.getOrderById(orderId);
          
          const cancelResult = await orderService.cancelOrder(orderId, '결제 실패로 인한 자동 취소');
          if (cancelResult.success) {
            // 장바구니 상태 복원
            if (orderData && (orderData as any).cart_id) {
              try {
                await tossPaymentsService.handlePaymentFailure(
                  (orderData as any).cart_id, 
                  orderData.order_type
                );
              } catch (restoreError) {
                console.error('Error restoring cart after payment failure:', restoreError);
              }
            }
          } else {
            console.warn('Failed to cancel pending order:', cancelResult.error);
            // 주문 취소 실패는 사용자에게 보여주지 않음 (백그라운드 처리)
          }
        } catch (error) {
          console.error('Error cancelling pending order:', error);
          // 주문 취소 실패는 사용자에게 보여주지 않음 (백그라운드 처리)
        }
      }

      showToast(errorMessage);
    };

    handlePaymentFailure();
  }, [searchParams, showToast]);

  const handleRetry = () => {
    // 장바구니로 돌아가기
    navigate('/cart');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <DesktopPageContainer>
      <div className="flex items-center justify-center min-h-[600px]">
        <div className="bg-white rounded-2xl p-12 max-w-md w-full shadow-lg border border-gray-200">
          <div className="text-center">
            {/* Error Icon */}
            <div className="mx-auto mb-6 w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-[28px] font-bold mb-4">결제 실패</h3>

            {/* Error Message */}
            <p className="text-gray-600 text-[16px] mb-6">
              {searchParams.get('message') || '결제 처리 중 문제가 발생했습니다'}
            </p>

            {/* Error Code */}
            {searchParams.get('code') && (
              <p className="text-[14px] text-gray-500 mb-8">
                오류 코드: {searchParams.get('code')}
              </p>
            )}

            {/* Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleRetry}
                className="w-full py-4 px-6 bg-green-500 text-white text-[18px] rounded-xl font-bold hover:bg-green-600 transition-colors"
              >
                다시 시도하기
              </button>
              <button
                onClick={handleGoHome}
                className="w-full py-4 px-6 border border-gray-300 text-gray-700 text-[18px] rounded-xl font-bold hover:bg-gray-50 transition-colors"
              >
                홈으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>
    </DesktopPageContainer>
  );
}